import * as React from "react";
import { ChevronsUpDownIcon, CheckIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface CategoryOption {
  label: string;
  value: string;
  color?: string;
}

interface CategoryGroup {
  label: string;
  options: CategoryOption[];
}

interface CategoryComboboxProps {
  groups: CategoryGroup[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  modal?: boolean;
}

export function CategoryCombobox({
  groups,
  value,
  onValueChange,
  placeholder = "Select category...",
  searchPlaceholder = "Search categories...",
  emptyMessage = "No category found.",
  className,
  disabled = false,
  modal = false,
}: CategoryComboboxProps) {
  const [open, setOpen] = React.useState(false);

  // Find the selected category across all groups
  const selectedCategory = React.useMemo(() => {
    for (const group of groups) {
      const found = group.options.find((option) => option.value === value);
      if (found) return found;
    }
    return null;
  }, [groups, value]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between", className)}
        >
          {selectedCategory ? (
            <div className="flex items-center">
              {selectedCategory.color && (
                <div
                  className="flex-shrink-0 w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: selectedCategory.color }}
                />
              )}
              <span className="truncate">{selectedCategory.label}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-full border border-primary",
                        value === option.value
                          ? "bg-primary text-white"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                      style={{
                        backgroundColor: option.color as string,
                      }}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
